import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";

import { CircularProgress, Grid, Tooltip } from "@mui/material";
import ShipeeziNewGrid, {
  Column,
  Constants,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import { differenceInDays, format } from "date-fns";
import { useTranslation } from "react-i18next";
import { MdOutlineCancel } from "react-icons/md";
import { useHistory } from "react-router-dom";

import ConfirmationModal from "../../../../../components/Dialogs/ConfirmationModal";
import { Color } from "../../../../../components/Form/Button";
import BadgeTransshipmentStatus, {
  TransshipmentStatus,
} from "../../../../../components/Generic/BadgeTransshipmentStatus";
import IconUserOctagon from "../../../../../components/Generic/Icons/IconUserOctagon";
import SubHeader from "../../../../../components/Generic/SubHeader";
import { PropHandles } from "../../../../../components/Generic/ViewJobComponent";
import {
  MoreOptions,
  MoreOptionsComponentHandles,
} from "../../../../../components/MoreOptionsComponent/MoreOptions";
import { PRIVATE_ROUTES } from "../../../../../constants";
import { Job } from "../../../../../models/job";
import { Sort } from "../../../../../models/shipeezi.table";
import { Task } from "../../../../../models/task";
import { Transshipment, TransshipmentType } from "../../../../../models/transshipment";
import { User } from "../../../../../models/user";
import { TaskStatus } from "../../../../../util/enums";
import { ChangeTransshipmentAssigneeModal } from "./ChangeTransshipmentAssigneeModal";
import { OrderView, OrderViewComponentHandler } from "./components/OrderViewComponent";
import TopPanel from "./components/TopPanel";
import { useTransshipmentActions } from "./hooks/useActions";
import useStyles, {
  CancelIcon,
  DescriptionModalConfirm,
  EditIcon,
  IconWrapper,
  LocationWrapper,
  OrderOption,
  PublishIcon,
  TransshipmentsGrid,
  ViewIcon,
} from "./styles";
import { TransshipmentViewModal } from "./TransshipmentViewModal";
import _ from "lodash";
import { useAppState } from "Context/StateContext";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { ResourceCanEnum, ResourceName, ResourceObjectTypeEnum } from "Util/enums/accessRule";
import { getOrderCustomerDisplayName } from "Util/names";
import { isMoving } from "Util/validation";
import { formatDateWithUserPref } from "Util/dateFormaterPreference";

export function TransshipmentsPage() {
  const history = useHistory();
  const {
    getTransshipments,
    handlePageSize,
    handleCurrentPage,
    handleSearch,
    handleSort,
    handleFilter,
    usePublishTransshipment,
    total,
    page,
    pageSize,
    setSorting,
    dateTo,
    dateFrom,
    setDateFrom,
    setDateTo,
    useUpdateTransshipmentStatus,
    selectedTransshipment,
    setSelectedTransshipment,
  } = useTransshipmentActions();
  const {
    refetch: refetchTransshipmentData,
    data: transshipmentsData,
    isFetching,
  } = getTransshipments();
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const [disableCreatedBySorting, setDisableCreatedBySorting] = useState<boolean>(false);
  const [disableAssignedToSorting, setDisableAssignedToSorting] = useState<boolean>(false);
  const [disableTransshipmentToSorting, setDisableTransshipmentToSorting] =
    useState<boolean>(false);
  const [disableTransshipmentFromSorting, setDisableTransshipmentFromSorting] =
    useState<boolean>(false);
  const [disableTaskStatusSorting, setDisableTaskStatusSorting] = useState<boolean>(false);
  const [changeTransshipmentLeader, setChangeTransshipmentLeader] = useState<Transshipment>();
  const [transshipmentCancel, setTransshipmentCancel] = useState<Transshipment>();
  const [openConfirmationCancelModal, setOpenConfirmationCancelModal] = useState<boolean>(false);
  const [openChangeAssigneeModal, setOpenChangeAssigneeModal] = useState<boolean>(false);
  const { currentUser, userPermissions } = useAppState();
  const [warehousesToView, setWarehousesToView] = useState<any[]>([]);
  const [transshipmentPermission, setTransshipmentPermission] = useState<{
    view: boolean;
    create: boolean;
    edit: boolean;
    delete: boolean;
    cancel: boolean;
    publish: boolean;
    changeAssignee: boolean;
  }>({
    view: false,
    create: false,
    edit: false,
    delete: false,
    cancel: false,
    publish: false,
    changeAssignee: false,
  });

  const sideViewRef = useRef<PropHandles>(null);
  const ordersNumberMenu = useRef<MoreOptionsComponentHandles>(null);
  const orderViewComponentRef = useRef<OrderViewComponentHandler>(null);

  const showWarehousesSelect = isMoving(currentUser?.company);
  const rightActions: string[] | undefined = showWarehousesSelect
    ? ["warehousesSelect", "handleNewRecord"]
    : undefined;

  const { mutate: handlePublishTransshipment, isLoading: isLoadingPublishTransshipment } =
    usePublishTransshipment();

  const { mutate: handleUpdateStatus, isLoading: isLoadingCancelTransshipment } =
    useUpdateTransshipmentStatus(() => setOpenConfirmationCancelModal(false));

  const handleCancelTransshipment = () => {
    if (transshipmentCancel) {
      handleUpdateStatus({
        transshipment: { id: transshipmentCancel?.id, status: TaskStatus.CANCELLED },
      });
    }
  };

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setTransshipmentPermission({
        view: hasPermission(userPermissions, FN_NAMES.FUNCTION_WAREHOUSING_TRANSSHIPMENT_VIEW),
        create:
          checkResourceCan(userPermissions, ResourceName.TRANSSHIPMENT, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_WAREHOUSING_TRANSSHIPMENT_ADD_NEW),
        edit:
          checkResourceCan(userPermissions, ResourceName.TRANSSHIPMENT, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_WAREHOUSING_TRANSSHIPMENT_EDIT),
        delete:
          checkResourceCan(userPermissions, ResourceName.TRANSSHIPMENT, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_WAREHOUSING_TRANSSHIPMENT_DELETE),
        cancel:
          checkResourceCan(userPermissions, ResourceName.TRANSSHIPMENT, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_WAREHOUSING_TRANSSHIPMENT_CANCEL),
        publish:
          checkResourceCan(userPermissions, ResourceName.TRANSSHIPMENT, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_WAREHOUSING_TRANSSHIPMENT_PUBLISH),
        changeAssignee:
          checkResourceCan(userPermissions, ResourceName.TRANSSHIPMENT, ResourceCanEnum.EDIT) &&
          hasPermission(
            userPermissions,
            FN_NAMES.FUNCTION_WAREHOUSING_TRANSSHIPMENT_CHANGE_ASSIGNEE
          ),
      });
      const storageBoxResourceId = userPermissions?.find((permission) => {
        return (
          permission?.objectType === ResourceObjectTypeEnum.RESOURCE &&
          permission?.objectDetails?.name === ResourceName.TRANSSHIPMENT
        );
      })?.objectDetails?.id;

      const warehouses = userPermissions
        ?.map((permission) => {
          if (
            permission?.objectType === ResourceObjectTypeEnum.FUNCTION &&
            permission?.objectDetails?.objectData === "warehouse" &&
            permission?.resourceId === storageBoxResourceId
          ) {
            return permission;
          }
        })
        ?.filter((permission) => !!permission);

      setWarehousesToView(warehouses);
    }
  }, [userPermissions]);

  useEffect(() => {
    if (transshipmentCancel?.id) {
      setOpenConfirmationCancelModal(true);
    }
  }, [transshipmentCancel]);

  const returnOrderLabel = useCallback((job?: Job) => {
    if (!job) return "";

    const jobSequence = job.sequence && Number(job.sequence) > 0 ? `/${job.sequence}` : "";
    const jobLabel = `${job.reference}${jobSequence}`;
    const customerName = getOrderCustomerDisplayName(job);

    return `${jobLabel} ${customerName?.trim().length && `- ${customerName}`}`;
  }, []);

  const returnLocationList = useCallback((locations?: string[]) => {
    if (!locations?.length) return "";

    return (
      <LocationWrapper>
        {locations?.map((location) => (
          <Tooltip key={location} title={location} placement="top">
            <span>{location}</span>
          </Tooltip>
        ))}
      </LocationWrapper>
    );
  }, []);

  const columns: Column[] = useMemo(
    () => [
      {
        name: "dueBy",
        title: translate("dueDateBy"),
        hideSearch: true,
        formatComponent: (date?: Date) => {
          if (date) {
            return (
              <span
                className={
                  differenceInDays(new Date(date), new Date()) < 0 ? classes.pastDueDate : ""
                }
              >
                {currentUser?.preferences
                  ? formatDateWithUserPref(date, currentUser.preferences, "dateOnly")
                  : ""}
              </span>
            );
          }

          return null;
        },
      },
      {
        name: "type",
        title: translate("type"),
        hideSearch: true,
        hideSort: true,
        formatText: (type?: TransshipmentType) =>
          (type?.name?.substring(0, 1).toUpperCase() || "") + (type?.name?.substring(1) || "") ||
          "",
      },
      {
        name: "reference",
        title: translate("reference"),
        hideSort: true,
      },
      {
        name: "createdBy",
        title: translate("requestedBy"),
        formatText: (createdBy?: User) => createdBy?.fullName || translate("system"),
        hideSort: disableCreatedBySorting,
        associationColumnSettings: {
          whenSearching: {
            clearSorting: setSorting,
            disableSorting: setDisableCreatedBySorting,
          },
          columnToSorting: "full_name",
        },
      },
      {
        name: "task",
        title: translate("orders"),
        hideSearch: true,
        formatComponent: (task: Task) => {
          if (task && task?.jobs) {
            return (
              <MoreOptions
                ref={ordersNumberMenu}
                title={translate("moreOrders")}
                manyOptionsToShow={2}
                data={task?.jobs?.sort((orderA, orderB) => {
                  if (orderA?.reference && orderB?.reference) {
                    if (orderA?.reference?.toLowerCase() > orderB?.reference?.toLowerCase()) {
                      return 1;
                    }
                    if (orderA?.reference?.toLowerCase() < orderB?.reference?.toLowerCase()) {
                      return -1;
                    }
                  }

                  return 0;
                })}
                returnLabel={(job: Job) => returnOrderLabel(job)}
                asComponent={(label: string, job: Job) => (
                  <OrderOption
                    onClick={() => {
                      ordersNumberMenu?.current?.setAnchor(null);
                      orderViewComponentRef?.current?.open(Number(job.id));
                    }}
                  >
                    {label}
                  </OrderOption>
                )}
              />
            );
          }

          return null;
        },
        hideSort: true,
      },
      {
        name: "processedFromLocations",
        title: translate("from"),
        hideSearch: true,
        hideSort: disableTransshipmentFromSorting,
        className: classes.maxWidthColumn,
        formatComponent: (processedFromLocations?: any) => {
          return returnLocationList(processedFromLocations);
        },
        associationColumnSettings: {
          whenSearching: {
            clearSorting: setSorting,
            disableSorting: setDisableTransshipmentFromSorting,
          },
          columnToSorting: "fromStorageBox",
        },
      },
      {
        name: "processedToLocations",
        title: translate("to"),
        hideSearch: true,
        hideSort: disableTransshipmentToSorting,
        className: classes.maxWidthColumn,
        formatComponent: (processedToLocations?: any[]) => {
          return returnLocationList(processedToLocations);
        },
        associationColumnSettings: {
          whenSearching: {
            clearSorting: setSorting,
            disableSorting: setDisableTransshipmentToSorting,
          },
          columnToSorting: "toStorageBox",
        },
      },
      {
        name: "leader",
        title: translate("assignedTo"),
        formatText: (leader?: User) => leader?.fullName || "-",
        hideSort: disableAssignedToSorting,
        associationColumnSettings: {
          whenSearching: {
            clearSorting: setSorting,
            disableSorting: setDisableAssignedToSorting,
          },
          columnToSorting: "full_name",
        },
      },
      {
        name: "status",
        title: translate("status"),
        hideSearch: true,
        hideSort: disableTaskStatusSorting,
        formatComponent: (status?: TransshipmentStatus) => {
          return <BadgeTransshipmentStatus value={status} />;
        },
        associationColumnSettings: {
          whenSearching: {
            clearSorting: setSorting,
            disableSorting: setDisableTaskStatusSorting,
          },
          columnToSorting: "status",
        },
      },
    ],
    [
      translate,
      classes,
      disableCreatedBySorting,
      disableAssignedToSorting,
      disableTransshipmentFromSorting,
      disableTransshipmentToSorting,
      disableTaskStatusSorting,
      returnLocationList,
      ordersNumberMenu,
      orderViewComponentRef,
    ]
  );

  const viewTransshipment = useCallback(
    (transshipmentId: number) => {
      const transshipmentToView = transshipmentsData.find(
        (transshipment: any) => Number(transshipment.id) === Number(transshipmentId)
      );

      return (
        <Tooltip title={translate("view")} placement="top">
          <div>
            <ViewIcon
              size={18}
              color="#475467"
              onClick={() => {
                setSelectedTransshipment(transshipmentToView);
                sideViewRef.current?.setOpenSideBar(true);
              }}
            />
          </div>
        </Tooltip>
      );
    },
    [transshipmentsData, translate, setSelectedTransshipment, sideViewRef]
  );

  const deleteTransshipment = () => {
    return null;
  };

  const changeTransshipmentAssignee = useCallback(
    (transshipmentId: number) => {
      const transshipment = transshipmentsData.find(
        (transshipment: any) => Number(transshipment.id) === Number(transshipmentId)
      );

      const isValidTaskStatus = (status?: string): status is TaskStatus => {
        return Object.values(TaskStatus).includes(status as TaskStatus);
      };

      const canChangeAssigneeLeader = (status: Transshipment["status"]): boolean => {
        if (!isValidTaskStatus(status)) {
          return false;
        }

        return [
          TaskStatus.DRAFT,
          TaskStatus.PUBLISHED,
          TaskStatus.STARTED,
          TaskStatus.IN_PROGRESS,
        ].includes(status);
      };

      const isAbleToChangeAssigneeLeader = canChangeAssigneeLeader(transshipment?.status);

      if (isAbleToChangeAssigneeLeader) {
        return (
          <Tooltip title={translate("changeAssignee")} placement="top">
            <IconWrapper
              onClick={() => {
                setChangeTransshipmentLeader(transshipment);
                setOpenChangeAssigneeModal(true);
              }}
            >
              <IconUserOctagon color={colors.gray600} />
            </IconWrapper>
          </Tooltip>
        );
      }

      return null;
    },
    [transshipmentsData]
  );

  const cancelTransshipment = useCallback(
    (transshipmentId: number) => {
      const transshipmentToCancel = transshipmentsData.find(
        (transshipment: any) => Number(transshipment.id) === Number(transshipmentId)
      );

      const canCancel = (transshipment?: Transshipment): boolean => {
        if (!transshipment) return false;

        const transshipmentStatusList = [TransshipmentStatus.PUBLISHED].map((value) =>
          value.toLowerCase()
        );

        const taskStatus = transshipmentToCancel?.task?.status?.toLowerCase() || "";
        const transshipmentStatus = transshipmentToCancel?.task?.status?.toLowerCase() || "";

        return (
          transshipmentStatusList.includes(taskStatus) ||
          transshipmentStatusList.includes(transshipmentStatus)
        );
      };

      if (canCancel(transshipmentToCancel)) {
        return (
          <Tooltip title={translate("cancelTransshipment")} placement="top">
            <div>
              <CancelIcon
                size={Constants.defaultGeneralActionsIconSize}
                data-cy="canceledButton"
                color="#F97066"
                onClick={() => setTransshipmentCancel(transshipmentToCancel)}
              />
            </div>
          </Tooltip>
        );
      }

      return null;
    },
    [transshipmentsData]
  );

  const editTransshipment = useCallback(
    (transshipmentId: number) => {
      const transshipmentToUpdate = transshipmentsData.find(
        (transshipment: any) => Number(transshipment.id) === Number(transshipmentId)
      );

      const canEdit = (transshipment?: Transshipment): boolean => {
        if (!transshipment) return false;

        const transshipmentStatusList = [
          TransshipmentStatus.DRAFT,
          TransshipmentStatus.CREATED,
        ].map((value) => value.toLowerCase());

        const taskStatus = transshipmentToUpdate?.task?.status?.toLowerCase() || "";
        const transshipmentStatus = transshipmentToUpdate?.task?.status?.toLowerCase() || "";

        return (
          transshipmentStatusList.includes(taskStatus) ||
          transshipmentStatusList.includes(transshipmentStatus)
        );
      };

      if (canEdit(transshipmentToUpdate)) {
        return (
          <Tooltip title={translate("edit")} placement="top">
            <div
              onClick={() =>
                history.push(`${PRIVATE_ROUTES.TRANSSHIPMENTS_MNT}/${transshipmentId}`)
              }
            >
              <EditIcon size={18} color="#475467" />
            </div>
          </Tooltip>
        );
      }

      return null;
    },
    [transshipmentsData, history, translate]
  );

  const publishTransshipment = useCallback(
    (transshipmentId: number) => {
      const transshipmentToPublish = transshipmentsData?.find(
        (transshipment: any) => Number(transshipment.id) === Number(transshipmentId)
      );

      if (
        transshipmentToPublish &&
        transshipmentToPublish?.task?.status?.toLowerCase() ===
          TransshipmentStatus.CREATED.toLowerCase()
      ) {
        if (
          selectedTransshipment &&
          selectedTransshipment.id === transshipmentId &&
          isLoadingPublishTransshipment
        ) {
          return <CircularProgress className={classes.loadingIcon} size={18} />;
        }

        return (
          <Tooltip title={translate("publish")} placement="top">
            <div
              onClick={() => {
                setSelectedTransshipment(transshipmentToPublish);
                handlePublishTransshipment({ transshipment: transshipmentToPublish });
              }}
            >
              <PublishIcon size={18} color="#475467" />
            </div>
          </Tooltip>
        );
      }

      return null;
    },
    [dateTo, dateFrom, setDateTo, setDateFrom]
  );

  const returnTopPanel = useCallback(() => {
    return (
      <TopPanel
        dateTo={dateTo}
        dateFrom={dateFrom}
        setDateTo={setDateTo}
        setDateFrom={setDateFrom}
      />
    );
  }, [dateTo, dateFrom, setDateTo, setDateFrom]);

  const handleRowActionsExtraByPermission = () => {
    let actions: RowActionsExtra[] = [];

    if (isShowAction(currentUser, transshipmentPermission.edit)) {
      actions?.push({
        title: translate("edit"),
        key: "handleEditTransshipment",
        buttonAndAction: editTransshipment,
      });
    }

    if (isShowAction(currentUser, transshipmentPermission.delete)) {
      actions?.push({
        title: translate("delete"),
        key: "handleDeleteTransshipment",
        buttonAndAction: deleteTransshipment,
      });
    }

    if (isShowAction(currentUser, transshipmentPermission.changeAssignee)) {
      actions?.push({
        title: translate("changeAssignee"),
        key: "handleChangeAssignee",
        buttonAndAction: changeTransshipmentAssignee,
      });
    }

    if (isShowAction(currentUser, transshipmentPermission.cancel)) {
      actions?.push({
        title: translate("cancel"),
        key: "handleCancelTransshipment",
        buttonAndAction: cancelTransshipment,
      });
    }

    if (isShowAction(currentUser, transshipmentPermission.publish)) {
      actions?.push({
        title: translate("publish"),
        key: "handlePublishTransshipment",
        buttonAndAction: publishTransshipment,
      });
    }

    if (isShowAction(currentUser, transshipmentPermission.view)) {
      actions?.push({
        title: translate("view"),
        key: "handleViewTransshipment",
        buttonAndAction: viewTransshipment,
      });
    }

    return actions;
  };

  return (
    <div data-cy="transshipmentsGrid">
      <SubHeader showWarehouseSelect warehousesToView={warehousesToView} />

      <TransshipmentsGrid>
        <ShipeeziNewGrid
          data={transshipmentsData || []}
          columns={columns}
          totalItems={total}
          loadingData={isFetching || transshipmentsData === undefined}
          emptyData={translate("noInformationToBeShown")}
          pageSizeList={[15, 40, 60, 100]}
          pageSize={pageSize}
          onChangePageSize={handlePageSize}
          currentPage={page}
          onNextPage={handleCurrentPage}
          onPreviousPage={handleCurrentPage}
          onGoToPage={handleCurrentPage}
          onSorting={(sorts: Sort[]) => handleSort(sorts)}
          showSelection={false}
          positionActionsColumn="right"
          generalActions={{
            orderBy: {
              left: ["handleSearch", "handleFilterBy", "handleTopPanel"],
              right: rightActions,
            },
            handleSearch: {
              onSearch: (filters) => handleSearch(filters),
            },
            handleFilterBy: {
              categories: [
                {
                  name: translate("status"),
                  column: "status",
                  options: TransshipmentStatus && Object.values(TransshipmentStatus),
                },
              ],
              onChange: handleFilter,
            },
            handleNewRecord: isShowAction(currentUser, transshipmentPermission.create)
              ? {
                  text: translate("newTransshipment"),
                  action: () => history.push(PRIVATE_ROUTES.TRANSSHIPMENTS_MNT),
                }
              : undefined,
            handleTopPanel: returnTopPanel(),
          }}
          rowActions={{
            orderBy: [
              "handleEditTransshipment",
              "handleViewTransshipment",
              "handleDeleteTransshipment",
              "handlePublishTransshipment",
              "handleChangeAssignee",
              "handleCancelTransshipment",
            ],
          }}
          rowActionsExtra={handleRowActionsExtraByPermission()}
        />
      </TransshipmentsGrid>

      {!_.isEmpty(selectedTransshipment) && (
        <TransshipmentViewModal
          transshipment={selectedTransshipment}
          loading={false}
          sideViewRef={sideViewRef}
        />
      )}

      {openConfirmationCancelModal && (
        <ConfirmationModal
          width="520px !important"
          open={openConfirmationCancelModal}
          closeModal={() => {
            setOpenConfirmationCancelModal(false);
            setTransshipmentCancel(undefined);
          }}
          type="error"
          title={translate("cancelTransshipment")}
          leftIconTitle={
            <MdOutlineCancel color="#F04438" size={Constants.defaultGeneralActionsIconSize} />
          }
          loading={isLoadingCancelTransshipment}
          customColorCancel={Color.TEXT_INFO}
          customColorSave={Color.ERROR}
          textCancel={translate("back")}
          textSubmit={translate("yesCancelTransshipment")}
          submit={handleCancelTransshipment}
        >
          <Grid container spacing={2} className="mt-2">
            <Grid item>
              <DescriptionModalConfirm>
                <span>
                  {translate("areYouSureYouWantToCancelTheTransshipment")}{" "}
                  {transshipmentCancel?.reference ?? ""}?
                </span>
              </DescriptionModalConfirm>
            </Grid>
          </Grid>
        </ConfirmationModal>
      )}
      {openChangeAssigneeModal && (
        <ChangeTransshipmentAssigneeModal
          isOpen={openChangeAssigneeModal}
          onCloseModal={() => {
            setOpenChangeAssigneeModal(false);
            setChangeTransshipmentLeader(undefined);
          }}
          transshipment={changeTransshipmentLeader}
          refetchTransshipment={refetchTransshipmentData}
        />
      )}

      <OrderView ref={orderViewComponentRef} />
    </div>
  );
}
